import { useMutation } from '@apollo/client'
import { CircleX, Loader, ShieldAlert } from 'lucide-react'
import { useSearchParams } from 'react-router'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { gql } from '@/lib/graphql/gql'
import type {
  InitiateSamlSsoReauthMutation,
  InitiateSamlSsoReauthMutationVariables,
} from '@/components/error-boundaries/fallbacks/saml-auth-required-fallback.types.gen'
import type { SamlAuthRequiredError } from '@/lib/errors/saml-auth-required-error'

const INITIATE_SAML_SSO_REAUTH_MUTATION = gql`
  mutation InitiateSamlSsoReauth(
    $destination: String!
    $originPath: String
    $idCompany: String
  ) {
    initiateSamlSsoLogin(
      destination: $destination
      originPath: $originPath
      idCompany: $idCompany
    ) {
      redirectUrl
    }
  }
`

const SAML_ERROR_MESSAGES: Record<string, string> = {
  invalid_response:
    'The SAML response from your identity provider was invalid.',
  invalid_signature: 'The SAML response signature could not be verified.',
  invalid_assertion: 'The SAML assertion was invalid or missing.',
  expired_assertion: 'The SAML assertion has expired.',
  invalid_audience: 'The SAML audience restriction did not match.',
  invalid_recipient: 'The SAML recipient did not match the expected URL.',
  missing_attributes:
    'Required attributes (firstName, lastName, email) were missing.',
  invalid_email: 'The email address in the SAML response was invalid.',
  user_not_found: 'User not found for the provided email.',
  saml_config_not_found: 'SAML configuration not found.',
  email_mismatch: 'SAML response email does not match the initiator email.',
  domain_mismatch:
    'SAML response email domain does not match the configured domain.',
}

const getSamlErrorMessage = (errorCode: string | null) => {
  if (!errorCode) {
    return null
  }
  return (
    SAML_ERROR_MESSAGES[errorCode] ??
    errorCode ??
    'An unknown error occurred during authentication.'
  )
}

export const SamlAuthRequiredFallback = ({
  error,
}: {
  error: SamlAuthRequiredError
}) => {
  const [searchParams] = useSearchParams()
  const errorParam = searchParams.get('error')
  const errorMessage = getSamlErrorMessage(errorParam)

  const [
    initiateSamlSsoReauth,
    { loading: isRedirecting, error: mutationError },
  ] = useMutation<
    InitiateSamlSsoReauthMutation,
    InitiateSamlSsoReauthMutationVariables
  >(INITIATE_SAML_SSO_REAUTH_MUTATION)

  const handleContinue = async () => {
    try {
      const result = await initiateSamlSsoReauth({
        variables: {
          destination: window.location.href,
          originPath: window.location.href,
          idCompany: error.idCompany,
        },
      })

      const redirectUrl = result.data?.initiateSamlSsoLogin?.redirectUrl
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
    } catch {
      // Error is captured by the mutation's error state
    }
  }

  return (
    <Card>
      <div className="flex flex-col items-center justify-center py-24">
        <div className="mx-auto size-24 rounded-full bg-linear-to-b from-primary/10 to-transparent flex items-center justify-center">
          <div className="size-14 bg-background flex items-center justify-center rounded-full border border-primary/10 shadow-xs shadow-primary/5">
            <ShieldAlert className="size-6 text-primary" />
          </div>
        </div>
        <h1 className="mt-4 text-2xl font-display text-center">
          Authentication required
        </h1>
        <p className="mt-2 max-w-md text-center text-muted-foreground">
          In order to access <strong>{error.companyName}</strong> you have to
          authenticate via the company&apos;s identity provider. When you
          continue, you will be redirected for authentication, and then
          automatically returned back to Sevalla.
        </p>

        {errorMessage && (
          <Alert variant="destructive" className="mt-6 max-w-md">
            <CircleX className="size-4" />
            <AlertDescription>
              <strong>Authentication failed.</strong>
              <span className="block mt-1">{errorMessage}</span>
            </AlertDescription>
          </Alert>
        )}

        {mutationError && (
          <Alert variant="destructive" className="mt-6 max-w-md">
            <CircleX className="size-4" />
            <AlertDescription>
              <strong>Something went wrong.</strong>
              <span className="block mt-1">
                {mutationError.message ?? 'Please try again.'}
              </span>
            </AlertDescription>
          </Alert>
        )}

        <Button
          className="mt-6"
          size="lg"
          onClick={handleContinue}
          disabled={isRedirecting}
        >
          {isRedirecting && (
            <Loader className="animate-spin" aria-hidden="true" />
          )}
          Continue
        </Button>
      </div>
    </Card>
  )
}
